/**
 * No-op rejection handler for sinatra thunks fired for their side effects.
 *
 * The sinatra auth thunks (`login`, `logout`, `fetchOrgToken`,
 * `updateUserAttributes`, the `appRoles`/`appUsers`/`appCIDRs` CRUD thunks, …)
 * dispatch their own `*_ERROR` action before rejecting, so on failure the redux
 * state is already updated. Callers that only trigger the thunk to populate that
 * state (and don't otherwise handle the returned promise) must still consume the
 * rejection — otherwise it becomes an unhandled promise rejection, which under a
 * parallel jest run can crash the worker and flake CI.
 *
 * Attach this to those best-effort calls, e.g.
 * `dispatch(fetchOrgToken(id, product)).catch(swallowThunkRejection)`.
 *
 * Do NOT use it where the caller needs to react to the failure (show a toast,
 * set local error state, redirect) — handle those explicitly instead.
 *
 * NOTE: this is a deliberate minimal stopgap, not the ideal design. The root
 * problem is that these thunks `throw dispatch(...Error)` — they reject with the
 * dispatched action object after already updating state. The better fix is to
 * stop them rejecting (dispatch the error action and return, or adopt RTK's
 * `createAsyncThunk` + opt-in `.unwrap()`), which would remove the need to catch
 * at every call site. That refactor is out of scope here (it changes the shared
 * thunk contract and every caller that relies on catching the throw); this
 * helper just stops the unhandled rejection from flaking CI in the meantime.
 */
export function swallowThunkRejection(): void {
  /* intentional no-op — see the doc comment above */
}
